import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, Eye, EyeOff, Upload, Image, DollarSign, Star, Search, ShoppingBag, Link, ExternalLink, Filter, Tag, MousePointerClick, RotateCcw, Lock } from "lucide-react";
import ImageUpload from "../../ImageUpload";
import { API, getImageUrl } from "./adminConfig";
import { AssessmentAnalytics, ContentActivityAnalytics, QuizLeadAnalytics } from "./AnalyticsTab";

const ReactQuill = lazy(() => import('react-quill-new'));

const MarketplaceForm = ({ item = null, onClose, onSuccess, categories }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    price: item?.price || '',
    category: item?.category || 'body',
    type: item?.type || item?.subcategory || 'supplement',  // Support both old and new field names
    benefit: item?.benefit || '',
    link: item?.link || '',
    image_url: item?.image_url || '',
    coupon_code: item?.coupon_code || '',
    learn_more_url: item?.learn_more_url || '',
    visible: item?.visible ?? true,
    member_only: item?.member_only ?? false,
    featured: item?.featured ?? false
  });
  const [loading, setLoading] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibraryImages, setImageLibraryImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [productBenefits, setProductBenefits] = useState([]);

  // Fetch product types and benefits on mount
  useEffect(() => {
    const fetchTypesAndBenefits = async () => {
      try {
        const [typesResponse, benefitsResponse] = await Promise.all([
          axios.get(`${API}/admin/product-types`),
          axios.get(`${API}/admin/product-benefits`)
        ]);
        setProductTypes(typesResponse.data);
        setProductBenefits(benefitsResponse.data);
      } catch (error) {
        console.error('Error fetching types and benefits:', error);
      }
    };
    
    fetchTypesAndBenefits();
  }, []);

  // Load images when modal opens
  useEffect(() => {
    if (showImageLibrary && imageLibraryImages.length === 0) {
      searchImageLibrary(); // Load all images instead of searching
    }
  }, [showImageLibrary]);

  // Dynamically load ReactQuill CSS (React 19 compatible version)
  useEffect(() => {
    import('react-quill-new/dist/quill.snow.css');
  }, []);

  // Rich Text Editor Configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': ['#1A5E5E', '#D9B94E', '#8B2635', '#002447', '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF'] }, 
       { 'background': ['#1A5E5E', '#D9B94E', '#8B2635', '#002447', '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF'] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ]
  };

  // Fix bullet list styling after Quill renders
  useEffect(() => {
    const fixBulletLists = () => {
      const bulletLists = document.querySelectorAll('.ql-editor ol[data-list="bullet"]');
      bulletLists.forEach(list => {
        list.style.listStyleType = 'disc';
        list.style.listStylePosition = 'outside';
        list.style.paddingLeft = '1.5em';
        list.style.counterReset = 'none';
        list.querySelectorAll('li').forEach(li => {
          li.style.listStyleType = 'disc';
          li.style.counterIncrement = 'none';
        });
      });
    };
    
    // Run immediately and after a delay
    fixBulletLists();
    const timer = setTimeout(fixBulletLists, 100);
    return () => clearTimeout(timer);
  }, [formData.content]);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 
    'blockquote', 'code-block', 'link', 'image'
  ];

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Convert relative URL to full URL for display
        const fullImageUrl = `${API}${response.data.image_url}`;
        setFormData({...formData, image_url: fullImageUrl});
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const searchImageLibrary = async (query = '') => {
    try {
      console.log('Marketplace form searching Image Library for:', query);
      setImageLibraryImages([]); // Clear previous results
      
      // Filter for images only (exclude videos)
      const params = { public_access: "true", file_type: "image" };
      if (query) params.search = query;
      
      const response = await axios.get(`${API}/admin/image-library`, { params });
      
      console.log('Marketplace form Image Library response:', response.data);
      
      // Transform the response to match the expected format
      const transformedImages = response.data.map(image => ({
        id: image.id,
        urls: {
          small: getImageUrl(image.url),
          regular: getImageUrl(image.url)
        },
        alt_description: image.description || image.name,
        name: image.name,
        description: image.description,
        tags: image.tags || []
      }));
      
      setImageLibraryImages(transformedImages);
      
    } catch (error) {
      console.error('Image Library API error:', error);
      setImageLibraryImages([]);
      toast.error('Error loading image library');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('[Marketplace Form] Submit started, item:', item);

    // Prepare submit data - ensure all string fields are properly converted
    const submitData = {
      name: String(formData.name || ''),
      description: String(formData.description || ''),
      price: typeof formData.price === 'number' ? formData.price : (formData.price && String(formData.price).trim() !== '' ? parseFloat(formData.price) : 0),
      category: String(formData.category || 'body'),
      type: String(formData.type || 'supplement'),
      benefit: formData.benefit || null,
      link: String(formData.link || ''),
      image_url: String(formData.image_url || ''),
      coupon_code: String(formData.coupon_code || ''),
      learn_more_url: String(formData.learn_more_url || ''),
      visible: Boolean(formData.visible),
      member_only: Boolean(formData.member_only),
      featured: Boolean(formData.featured)
    };

    try {
      // Always use POST to create/update (upsert by including id)
      const payload = item ? { ...submitData, id: item.id } : submitData;
      console.log('[Marketplace Form] Sending request to:', `${API}/admin/marketplace`);
      console.log('[Marketplace Form] Payload:', payload);
      
      const response = await axios.post(`${API}/admin/marketplace`, payload, { timeout: 30000 });
      
      console.log('[Marketplace Form] Response received:', response.data);
      
      if (item) {
        toast.success("Product updated successfully!");
      } else {
        toast.success("Product created successfully!");
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('[Marketplace Form] Error saving product:', error);
      console.error('[Marketplace Form] Error response:', error.response?.data);
      console.error('[Marketplace Form] Submit data:', submitData);
      
      // Show more specific error message
      let errorMessage = "Unknown error occurred";
      
      if (error.response?.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData.detail) {
          if (Array.isArray(responseData.detail)) {
            // Validation errors are usually in array format
            errorMessage = responseData.detail.map(err => 
              `${err.loc ? err.loc.join('.') : 'Field'}: ${err.msg}`
            ).join(', ');
          } else {
            errorMessage = responseData.detail;
          }
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else {
          errorMessage = JSON.stringify(responseData);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`Error saving product: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white border-burgundy/20 max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="font-serif text-burgundy flex justify-between items-center">
          {item ? 'Edit Product' : 'Add New Product'}
          <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Product Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Price ($) <span className="text-sm font-normal text-burgundy/60">(Optional for vendors)</span></label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="Leave empty for vendor listings"
              />
            </div>
          </div>

          {/* Description with Rich Text Editor */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Description</label>
            <Suspense fallback={
              <div className="border border-burgundy/30 rounded-lg p-4 min-h-[200px] bg-white flex items-center justify-center">
                <div className="text-burgundy/60">Loading editor...</div>
              </div>
            }>
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: '200px' }}
                placeholder="Write your product description here with rich formatting (bold, colors, lists, etc.)..."
                className="bg-white [&_.ql-editor]:text-black [&_.ql-editor]:min-h-[150px]"
              />
            </Suspense>
            <p className="text-xs text-burgundy/60 mt-2">
              Use the toolbar to format text: <strong>bold</strong>, <em>italic</em>, colors, bullet lists, and more
            </p>
          </div>

          {/* Product Image */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Product Image</label>
            <div className="space-y-3">
              {formData.image_url && (
                <div className="relative">
                  <img 
                    src={formData.image_url} 
                    alt="Product" 
                    className="w-full h-48 object-cover rounded-lg border-2 border-burgundy/20"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setFormData({...formData, image_url: ''})}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {/* File Upload */}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('image-upload').click()}
                    disabled={uploading}
                    className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-burgundy border-t-transparent rounded-full mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Image
                      </>
                    )}
                  </Button>
                </div>
                
                {/* URL Input */}
                <Input
                  type="url"
                  placeholder="Or enter image URL"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                />
                
                {/* Unsplash Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowImageLibrary(true)}
                  className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                >
                  <Image className="h-4 w-4 mr-2" />
                  Image Library
                </Button>
              </div>
            </div>
          </div>

          {/* Categories, Type, and Benefit */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Category</label>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="body">Body</SelectItem>
                  <SelectItem value="mind">Mind</SelectItem>
                  <SelectItem value="vitality">Vitality</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Type</label>
              <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {productTypes.map(type => (
                    <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Benefit (Optional)</label>
              <Select value={formData.benefit || 'none'} onValueChange={(value) => setFormData({...formData, benefit: value === 'none' ? '' : value})}>
                <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                  <SelectValue placeholder="Select benefit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {productBenefits.map(benefit => (
                    <SelectItem key={benefit.id} value={benefit.name}>{benefit.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Visibility and Access Control */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="visible"
                checked={formData.visible}
                onChange={(e) => setFormData({...formData, visible: e.target.checked})}
                className="rounded border-burgundy/30"
              />
              <label htmlFor="visible" className="text-sm font-medium text-burgundy">Visible to users</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="member-only"
                checked={formData.member_only}
                onChange={(e) => setFormData({...formData, member_only: e.target.checked})}
                className="rounded border-burgundy/30"
              />
              <label htmlFor="member-only" className="text-sm font-medium text-burgundy flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Member Only
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                className="rounded border-burgundy/30"
              />
              <label htmlFor="featured" className="text-sm font-medium text-burgundy flex items-center gap-1">
                <Star className="h-3 w-3" />
                Featured Product
              </label>
            </div>
          </div>

          {/* Product Link and Coupon */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Product Link</label>
              <Input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({...formData, link: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="https://example.com/product"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Coupon Code (Optional)</label>
              <Input
                value={formData.coupon_code}
                onChange={(e) => setFormData({...formData, coupon_code: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="SAVE20"
              />
            </div>
          </div>

          {/* Learn More Link */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Learn More Link (Optional)</label>
            <Input
              type="url"
              value={formData.learn_more_url}
              onChange={(e) => setFormData({...formData, learn_more_url: e.target.value})}
              className="border-burgundy/30 focus:border-gold"
              placeholder="Link to content on your site (e.g., /content/article)"
            />
            <p className="text-xs text-burgundy/60 mt-1">Link to related content, articles, or pages on your site</p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-burgundy hover:bg-burgundy/80 text-cream">
              {loading ? 'Saving...' : (item ? 'Update Product' : 'Create Product')}
            </Button>
          </div>
        </form>

        {/* Unsplash Modal */}
        {showImageLibrary && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-4xl mx-4 max-h-[80vh] overflow-auto">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  Choose Product Image
                  <Button variant="ghost" onClick={() => setShowImageLibrary(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Search for product images..."
                    onChange={(e) => searchImageLibrary(e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {imageLibraryImages.map((img) => (
                    <div
                      key={img.id}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        const imageUrl = img.urls.regular;
                        setFormData({...formData, image_url: imageUrl});
                        setShowImageLibrary(false);
                      }}
                    >
                      <img
                        src={img.urls.small}
                        alt={img.alt_description}
                        className="w-full h-32 object-cover rounded-lg border-2 border-burgundy/20"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Coupons Tab Component

const MarketplaceTab = ({ marketplaceItems, fetchMarketplaceItems }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newSubcategory, setNewSubcategory] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('body');
  const [marketplaceCategories, setMarketplaceCategories] = useState({});
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');

  useEffect(() => {
    fetchMarketplaceCategories();
  }, []);

  const fetchMarketplaceCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/marketplace-categories`);
      setMarketplaceCategories(response.data || {});
    } catch (error) {
      console.error('Error fetching marketplace categories:', error);
      toast.error('Error loading marketplace categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Get unique product types from items
  const productTypes = [...new Set(marketplaceItems.map(item => item.type).filter(Boolean))];

  // Get unique categories from items
  const productCategories = [...new Set(marketplaceItems.map(item => item.category).filter(Boolean))];

  // Filter marketplace items based on search and filters
  const filteredItems = marketplaceItems.filter(item => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      item.name?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower);

    // Category filter
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;

    // Type filter
    const matchesType = filterType === 'all' || item.type === filterType;

    // Visibility filter
    const matchesVisibility = filterVisibility === 'all' || 
      (filterVisibility === 'visible' && item.visible !== false) ||
      (filterVisibility === 'hidden' && item.visible === false);

    return matchesSearch && matchesCategory && matchesType && matchesVisibility;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterCategory('all');
    setFilterType('all');
    setFilterVisibility('all');
  };

  const hasActiveFilters = searchQuery || filterCategory !== 'all' || filterType !== 'all' || filterVisibility !== 'all';

  const addSubcategory = async () => {
    if (!newSubcategory.trim() || !selectedMainCategory) {
      toast.error('Please enter a subcategory name and select a main category');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/marketplace-categories/${selectedMainCategory}/subcategories`,
        { name: newSubcategory.trim() }
      );
      
      setMarketplaceCategories(response.data.categories);
      setNewSubcategory('');
      toast.success('Subcategory added successfully!');
    } catch (error) {
      console.error('Error adding subcategory:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Subcategory already exists');
      } else {
        toast.error('Error adding subcategory');
      }
    }
  };

  const removeSubcategory = async (mainCategory, subcategoryName) => {
    if (!window.confirm(`Are you sure you want to remove "${subcategoryName}" from ${mainCategory}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API}/admin/marketplace-categories/${mainCategory}/subcategories/${subcategoryName}`
      );
      
      setMarketplaceCategories(response.data.categories);
      toast.success('Subcategory removed successfully!');
    } catch (error) {
      console.error('Error removing subcategory:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cannot remove subcategory - products are using it');
      } else {
        toast.error('Error removing subcategory');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Marketplace Management</h2>
        <Button onClick={() => setShowAddForm(true)} className="bg-burgundy hover:bg-burgundy/80 text-cream">
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="bg-cream/50 border border-burgundy/20 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-burgundy font-medium">
          <Filter className="w-4 h-4" />
          <span>Search & Filters</span>
          {hasActiveFilters && (
            <Badge className="bg-burgundy text-cream text-xs">
              {filteredItems.length} of {marketplaceItems.length} products
            </Badge>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search Input */}
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-burgundy/50" />
            <Input
              placeholder="Search by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-burgundy/30 focus:border-burgundy"
            />
          </div>

          {/* Category Filter */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="border-burgundy/30">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {productCategories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="border-burgundy/30">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {productTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Visibility Filter */}
          <Select value={filterVisibility} onValueChange={setFilterVisibility}>
            <SelectTrigger className="border-burgundy/30">
              <SelectValue placeholder="All Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Visibility</SelectItem>
              <SelectItem value="visible">Visible Only</SelectItem>
              <SelectItem value="hidden">Hidden Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Marketplace Form Modal */}
      {(showAddForm || editingItem) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto">
            <MarketplaceForm
              item={editingItem}
              onClose={() => {
                setShowAddForm(false);
                setEditingItem(null);
              }}
              onSuccess={() => {
                fetchMarketplaceItems();
                setShowAddForm(false);
                setEditingItem(null);
              }}
              categories={marketplaceCategories}
            />
          </div>
        </div>
      )}

      {/* Product Types & Benefits Management */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Product Types & Benefits</CardTitle>
          <CardDescription>Manage available product types and benefit categories for marketplace filtering</CardDescription>
        </CardHeader>
        <CardContent>
          <ProductTypesAndBenefitsManager />
        </CardContent>
      </Card>

      {/* Products List */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12 text-burgundy/60">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No products found</p>
          <p className="text-sm">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredItems.map(item => (
            <Card key={item.id} className="bg-white border-burgundy/20">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {/* Product Image */}
                    {item.image_url && (
                      <div className="mb-4">
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-32 h-32 object-cover rounded-lg border-2 border-burgundy/20"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-burgundy">{item.name}</h3>
                      <Badge className="bg-gold/20 text-gold">${item.price}</Badge>
                      <Badge className={`${item.visible ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}`}>
                        {item.visible ? 'Visible' : 'Hidden'}
                      </Badge>
                      {item.coupon_code && (
                        <Badge className="bg-orange-100 text-orange-800 font-mono">
                          {item.coupon_code}
                        </Badge>
                      )}
                    </div>
                    <div 
                      className="text-burgundy/70 mb-3 rich-text-content" 
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                    <div className="flex gap-2 mb-2">
                      <Badge variant="outline" className="border-burgundy text-burgundy">
                        {item.category}
                      </Badge>
                      {item.type && (
                        <Badge variant="outline" className="border-dark-teal text-dark-teal">
                          Type: {item.type}
                        </Badge>
                      )}
                      {item.benefit && (
                        <Badge variant="outline" className="border-gold text-gold">
                          Benefit: {item.benefit}
                        </Badge>
                      )}
                    </div>
                    {item.link && (
                      <a 
                        href={item.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        View Product Link →
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingItem(item)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this product?')) {
                          try {
                            await axios.delete(`${API}/admin/marketplace/${item.id}`);
                            toast.success('Product deleted successfully');
                            fetchMarketplaceItems();
                          } catch (error) {
                            toast.error('Error deleting product');
                            console.error('Delete error:', error);
                          }
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Tracking Tab Component
const ANALYTICS_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'coupons', label: 'Coupons' },
  { id: 'users', label: 'Users' },
  { id: 'content', label: 'Content' },
  { id: 'products', label: 'Products' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'leads', label: 'Leads' },
  { id: 'activity', label: 'Activity' },
];

const TrackingTab = () => {
  const [referralStats, setReferralStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetchTrackingData();
  }, []);

  // Track which section is in view
  useEffect(() => {
    const observers = [];
    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0.1,
    });
    ANALYTICS_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) { observer.observe(el); observers.push(el); }
    });
    return () => observers.forEach((el) => observer.unobserve(el));
  }, [loading]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fetchTrackingData = async () => {
    try {
      const [referralResponse, activityResponse] = await Promise.all([
        axios.get(`${API}/admin/referral-stats`),
        axios.get(`${API}/admin/recent-activity`)
      ]);
      
      setReferralStats(referralResponse.data);
      setRecentActivity(activityResponse.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      toast.error('Error loading tracking data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading tracking data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sticky Section Nav */}
      <div className="sticky top-0 z-10 bg-cream/95 backdrop-blur-sm border-b border-burgundy/10 -mx-1 px-1 pb-2 pt-1" data-testid="analytics-section-nav">
        <div className="flex flex-wrap gap-1">
          {ANALYTICS_SECTIONS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              data-testid={`analytics-nav-${id}`}
              className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                activeSection === id
                  ? 'bg-burgundy text-cream font-medium shadow-sm'
                  : 'text-burgundy/60 hover:bg-burgundy/10 hover:text-burgundy'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview: Referral & Coupon Summary Stats */}
      <div id="overview" className="scroll-mt-16 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-burgundy">{referralStats?.total_referrals || 0}</div>
            <p className="text-xs text-burgundy/60">All time referrals</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Successful Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{referralStats?.successful_referrals || 0}</div>
            <p className="text-xs text-burgundy/60">
              {referralStats?.referral_conversion_rate ? `${referralStats.referral_conversion_rate.toFixed(1)}% conversion` : '0% conversion'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Coupon Codes Used</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gold">
              {referralStats?.coupon_usage ? Object.keys(referralStats.coupon_usage).length : 0}
            </div>
            <p className="text-xs text-burgundy/60">Active coupon codes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">New Users (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-dark-teal">{recentActivity?.summary?.new_users_last_30_days || 0}</div>
            <p className="text-xs text-burgundy/60">Last 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Referrers */}
      <Card id="referrals" className="scroll-mt-16">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Top Referrers</CardTitle>
          <CardDescription>Users who have made the most successful referrals</CardDescription>
        </CardHeader>
        <CardContent>
          {referralStats?.top_referrers && referralStats.top_referrers.length > 0 ? (
            <div className="space-y-3">
              {referralStats.top_referrers.slice(0, 10).map(([email, count], index) => (
                <div key={email} className="flex items-center justify-between p-3 bg-burgundy/5 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Badge className="bg-burgundy text-white">#{index + 1}</Badge>
                    <span className="font-medium text-burgundy">{email}</span>
                  </div>
                  <Badge variant="outline" className="border-gold text-gold">
                    {count} referrals
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-8">No referral data available yet</p>
          )}
        </CardContent>
      </Card>

      {/* Referral Relationships */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Referral Relationships</CardTitle>
          <CardDescription>Track who referred whom - perfect for sending thank you emails!</CardDescription>
        </CardHeader>
        <CardContent>
          {referralStats?.recent_referrals && referralStats.recent_referrals.length > 0 ? (
            <div className="space-y-4">
              {referralStats.recent_referrals.map((referral, index) => (
                <div key={index} className="border border-burgundy/20 rounded-lg p-4 bg-burgundy/5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">New Member</Badge>
                        <span className="font-medium text-burgundy">{referral.referred_email}</span>
                      </div>
                      <div className="text-sm text-burgundy/70">
                        Joined: {new Date(referral.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-burgundy">Referred by:</div>
                      <div className="font-semibold text-dark-teal">{referral.referrer_email}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-burgundy/60">
                    <span>💝 Send thank you email to referrer</span>
                    <Badge variant="outline" className={referral.bonus_applied ? 'border-emerald-500 text-emerald-600' : 'border-gold text-gold'}>
                      {referral.bonus_applied ? '✅ 50% discount applied' : '⏳ Pending activation'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-8">No referral relationships found yet</p>
          )}
        </CardContent>
      </Card>

      {/* Active Coupon Usage (excluding REFERRAL10) */}
      <Card id="coupons" className="scroll-mt-16">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Active Coupon Usage</CardTitle>
          <CardDescription>Track which coupon codes are currently being used</CardDescription>
        </CardHeader>
        <CardContent>
          {referralStats?.coupon_usage && Object.keys(referralStats.coupon_usage).filter(code => code !== 'REFERRAL10').length > 0 ? (
            <div className="space-y-4">
              {Object.entries(referralStats.coupon_usage)
                .filter(([code]) => code !== 'REFERRAL10')
                .map(([code, data]) => (
                <div key={code} className="border border-burgundy/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-burgundy text-white font-mono text-lg px-3 py-1">{code}</Badge>
                    <Badge variant="outline" className="border-emerald-500 text-emerald-600">
                      {data.count} uses
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-burgundy">Used by:</p>
                    <div className="flex flex-wrap gap-2">
                      {data.users.slice(0, 5).map((user, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {user}
                        </Badge>
                      ))}
                      {data.users.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{data.users.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-8">No active coupon usage found</p>
          )}
        </CardContent>
      </Card>

      {/* Recent User Activity */}
      <Card id="users" className="scroll-mt-16">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Recent User Registrations</CardTitle>
          <CardDescription>Latest users who joined the platform</CardDescription>
        </CardHeader>
        <CardContent>
          {recentActivity?.recent_users && recentActivity.recent_users.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.recent_users.slice(0, 10).map((user, index) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-burgundy/5 rounded-lg">
                  <div className="space-y-1">
                    <div className="font-medium text-burgundy">{user.name}</div>
                    <div className="text-sm text-burgundy/70">{user.email}</div>
                  </div>
                  <div className="text-right space-y-1">
                    <Badge className={
                      user.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                      user.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                      user.subscription_status === 'canceled' ? 'bg-red-100 text-red-800' :
                      'bg-purple-100 text-purple-800'
                    }>
                      {user.subscription_status}
                    </Badge>
                    <div className="text-xs text-burgundy/50">
                      {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-8">No recent users found</p>
          )}
        </CardContent>
      </Card>

      {/* Popular Content */}
      <div id="content" className="scroll-mt-16">
        <PopularContentSection />
      </div>

      {/* Popular Products */}
      <div id="products" className="scroll-mt-16">
        <PopularProductsSection />
      </div>

      {/* Assessment Analytics */}
      <div id="assessments" className="scroll-mt-16 mt-8">
        <h2 className="text-xl font-serif font-bold text-burgundy mb-4 border-b-2 border-gold pb-2">Wellness Assessment Results</h2>
        <AssessmentAnalytics />
      </div>

      {/* Quiz Lead Analytics */}
      <div id="leads" className="scroll-mt-16 mt-8">
        <h2 className="text-xl font-serif font-bold text-burgundy mb-4 border-b-2 border-gold pb-2">Quiz Lead Capture</h2>
        <QuizLeadAnalytics />
      </div>

      {/* Content Activity */}
      <div id="activity" className="scroll-mt-16 mt-8">
        <h2 className="text-xl font-serif font-bold text-burgundy mb-4 border-b-2 border-gold pb-2">Content Activity Tracking</h2>
        <ContentActivityAnalytics />
      </div>
    </div>
  );
};

// Popular Content Section Component
const PopularContentSection = () => {
  const [popularContent, setPopularContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPopularContent();
  }, []);

  const fetchPopularContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/popular-content?limit=10`);
      setPopularContent(response.data);
    } catch (error) {
      console.error('Error fetching popular content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-burgundy">Loading popular content...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-burgundy">Most Viewed Content</CardTitle>
        <CardDescription>Educational content with the most views</CardDescription>
      </CardHeader>
      <CardContent>
        {popularContent && popularContent.length > 0 ? (
          <div className="space-y-3">
            {popularContent.map((content, index) => (
              <div key={content.id} className="flex items-center justify-between p-3 bg-burgundy/5 rounded-lg hover:bg-burgundy/10 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gold text-white font-bold text-sm">
                    #{index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-burgundy truncate">{content.title}</div>
                    <div className="text-sm text-burgundy/70">
                      {content.category} • {content.content_type}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-burgundy/20 text-burgundy">
                    <Eye className="w-3 h-3 mr-1" />
                    {content.click_count} views
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-burgundy/60 text-center py-8">No content views tracked yet</p>
        )}
      </CardContent>
    </Card>
  );
};

// Popular Products Section Component
const PopularProductsSection = () => {
  const [popularProducts, setPopularProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPopularProducts();
  }, []);

  const fetchPopularProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/popular-products?limit=10`);
      setPopularProducts(response.data);
    } catch (error) {
      console.error('Error fetching popular products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-burgundy">Loading popular products...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-burgundy">Most Clicked Products</CardTitle>
        <CardDescription>Marketplace products with the most affiliate clicks</CardDescription>
      </CardHeader>
      <CardContent>
        {popularProducts && popularProducts.length > 0 ? (
          <div className="space-y-3">
            {popularProducts.map((product, index) => (
              <div key={product.id} className="flex items-center justify-between p-3 bg-burgundy/5 rounded-lg hover:bg-burgundy/10 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gold text-white font-bold text-sm">
                    #{index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-burgundy truncate">{product.name}</div>
                    <div className="text-sm text-burgundy/70">
                      {product.category} • ${product.price}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-dark-teal/20 text-dark-teal">
                    <MousePointerClick className="w-3 h-3 mr-1" />
                    {product.click_count} clicks
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-burgundy/60 text-center py-8">No product clicks tracked yet</p>
        )}
      </CardContent>
    </Card>
  );
};


// Homepage Carousel Tab Component

const ProductTypesAndBenefitsManager = () => {
  const [productTypes, setProductTypes] = useState([]);
  const [productBenefits, setProductBenefits] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [newBenefitName, setNewBenefitName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTypesAndBenefits();
  }, []);

  const fetchTypesAndBenefits = async () => {
    try {
      const [typesResponse, benefitsResponse] = await Promise.all([
        axios.get(`${API}/admin/product-types`),
        axios.get(`${API}/admin/product-benefits`)
      ]);
      setProductTypes(typesResponse.data);
      setProductBenefits(benefitsResponse.data);
    } catch (error) {
      console.error('Error fetching types and benefits:', error);
      toast.error('Error loading types and benefits');
    } finally {
      setLoading(false);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Please enter a type name');
      return;
    }

    try {
      await axios.post(`${API}/admin/product-types`, { name: newTypeName.trim() });
      toast.success('Product type added successfully!');
      setNewTypeName('');
      fetchTypesAndBenefits();
    } catch (error) {
      console.error('Error adding product type:', error);
      toast.error(error.response?.data?.detail || 'Error adding product type');
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this product type?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/product-types/${typeId}`);
      toast.success('Product type deleted successfully!');
      fetchTypesAndBenefits();
    } catch (error) {
      console.error('Error deleting product type:', error);
      toast.error('Error deleting product type');
    }
  };

  const handleAddBenefit = async () => {
    if (!newBenefitName.trim()) {
      toast.error('Please enter a benefit name');
      return;
    }

    try {
      await axios.post(`${API}/admin/product-benefits`, { name: newBenefitName.trim() });
      toast.success('Product benefit added successfully!');
      setNewBenefitName('');
      fetchTypesAndBenefits();
    } catch (error) {
      console.error('Error adding product benefit:', error);
      toast.error(error.response?.data?.detail || 'Error adding product benefit');
    }
  };

  const handleDeleteBenefit = async (benefitId) => {
    if (!window.confirm('Are you sure you want to delete this product benefit?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/product-benefits/${benefitId}`);
      toast.success('Product benefit deleted successfully!');
      fetchTypesAndBenefits();
    } catch (error) {
      console.error('Error deleting product benefit:', error);
      toast.error('Error deleting product benefit');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading...</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Product Types Section */}
      <div className="space-y-4">
        <h4 className="font-semibold text-burgundy">Product Types</h4>
        <p className="text-sm text-burgundy/60">Types help users filter by product format (e.g., supplement, peptide, nootropic)</p>
        
        {/* Display existing types */}
        <div className="space-y-2">
          {productTypes.length === 0 ? (
            <p className="text-burgundy/50 italic text-sm">No product types yet</p>
          ) : (
            productTypes.map(type => (
              <div key={type.id} className="flex items-center justify-between p-2 border border-burgundy/20 rounded">
                <Badge variant="outline" className="border-burgundy text-burgundy">
                  {type.name}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteType(type.id)}
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))
          )}
        </div>

        {/* Add new type */}
        <div className="flex gap-2">
          <Input
            placeholder="New type name (e.g., peptide)"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            className="border-burgundy/30 focus:border-gold"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddType();
              }
            }}
          />
          <Button 
            onClick={handleAddType}
            type="button"
            className="bg-gold hover:bg-gold/80 text-burgundy whitespace-nowrap"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Product Benefits Section */}
      <div className="space-y-4">
        <h4 className="font-semibold text-burgundy">Product Benefits</h4>
        <p className="text-sm text-burgundy/60">Benefits help users filter by desired outcome (e.g., energy, sleep, focus)</p>
        
        {/* Display existing benefits */}
        <div className="space-y-2">
          {productBenefits.length === 0 ? (
            <p className="text-burgundy/50 italic text-sm">No product benefits yet</p>
          ) : (
            productBenefits.map(benefit => (
              <div key={benefit.id} className="flex items-center justify-between p-2 border border-burgundy/20 rounded">
                <Badge variant="outline" className="border-dark-teal text-dark-teal">
                  {benefit.name}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteBenefit(benefit.id)}
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))
          )}
        </div>

        {/* Add new benefit */}
        <div className="flex gap-2">
          <Input
            placeholder="New benefit name (e.g., energy)"
            value={newBenefitName}
            onChange={(e) => setNewBenefitName(e.target.value)}
            className="border-burgundy/30 focus:border-gold"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddBenefit();
              }
            }}
          />
          <Button 
            onClick={handleAddBenefit}
            type="button"
            className="bg-gold hover:bg-gold/80 text-burgundy whitespace-nowrap"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};


export { MarketplaceTab, MarketplaceForm, TrackingTab, ProductTypesAndBenefitsManager };
export default MarketplaceTab;
